import React, { useState, useRef, memo } from 'react'
import { useHistory } from 'react-router-dom'
import { ButtonBase } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import cn from 'classnames'
import { isEmpty } from 'lodash'

import { ReactComponent as ShoppingCartOutlinedIcon } from '../assets/icons/Cart.svg'
import { ReactComponent as MobileShoppingCartOutlinedIcon } from '../assets/icons/mobileCart.svg'

import ConfirmModalV2 from '../components/ConfirmModalV2'

import DeviceSubtypes from '../utils/deviceSubtypes'

import { refreshConfig } from '../actions/config'
import { logout } from '../actions/attendant'
import { clearCart } from '../actions/cart'
import { clearOrderInProgress } from '../actions/order'

import { getOrderInProgress } from '../selectors/order'
import { getDeviceSubtype, getResourceList } from '../selectors/config'
import { hasCartItems } from '../selectors/cart'
import { getActiveAttendant, getAttendants } from '../selectors/attendant'
import { getIsKiosk, getRequireManagerPinForConfiguration } from '../selectors/menus'

import styles from './NavMenu.module.scss'
import useGetIsMobileScreen from '../hooks/useGetIsMobileScreen'

import { isTabbed } from '../utils/orderStates'
import useSpinner from '../context/hooks/useSpinner'
import { setRoute } from '../VNRoutes/ActionCreators'
import { setManagerApprovalPrompt } from '../VNManagerApproval/ActionCreators'
import { getNetworkAvailableValue } from '../utils/networkConnected'
import { setConnectionRequiredDialog } from '../VNDialogs/ActionCreators'
import { APPLICATION_STRINGS } from '../strings'
import { setShouldIncrementOrderNumber } from '../VNOrders/ActionCreators'
import { refreshCache } from '../actions/appState'
import { MODES } from '../VNMode/Reducer'
import { getDeviceMode } from '../VNMode/Selectors'
import { resetCFDrequest, sendCFDScreenNav } from '../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../VNCustomerFacingDisplay/Reducer'
import { setTransferTabModal } from '../VNDialogs/ActionCreators'
import { resetCFD } from '../VNCustomerFacingDisplay/ActionCreators'
import { isE700Device } from '../VNAndroidSDK/Utils'
import { ToastManager } from '../utils/toastManager'

const NavMenu = ({ onToggle, className }) => {
  const history = useHistory()
  const classNames = cn(styles.navMenu, className)
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [showConfirmAbandonCartModal, setShowConfirmAbandonCartModal] = useState(false)
  const isMobileViewPort = useGetIsMobileScreen()
  const networkConnected = getNetworkAvailableValue()

  // selectors
  const orderInProgress = useSelector(getOrderInProgress)
  const requireManagerPinForConfiguration = useSelector(getRequireManagerPinForConfiguration)
  const cfdMode = useSelector(getDeviceMode)
  const isKiosk = useSelector(getIsKiosk)
  const resourceList = Object.keys(useSelector(getResourceList))
  const attendantsExist = !isEmpty(useSelector(getAttendants))
  const curActiveAttendant = useSelector(getActiveAttendant)
  const attendantName = curActiveAttendant?.name ?? ''
  const deviceSubtype = useSelector(getDeviceSubtype)
  const cartItemsExist = useSelector(hasCartItems)

  let confirmAbandonCartElement = null
  const abandonCartAction = useRef(() => {})

  const { startSpinner } = useSpinner()
  const dispatch = useDispatch()

  let logoutModal = null

  const checkLocationAndStartSpinner = (to) => {
    //this avoids starting the spinner if user is already in the screen being routed
    if (history.location.pathname !== to) {
      startSpinner()
    }
  }

  const resetTransferTabs = () => {
    dispatch(setTransferTabModal({ open: false }))
  }

  const onClickLogout = () => {
    if (cfdMode === MODES.POS) {
      sendCFDScreenNav(CFD_SCREEN.IDLE)
      resetCFDrequest()
    }
    dispatch(setShouldIncrementOrderNumber())
    dispatch(logout())
    dispatch(setRoute('/user-login'))
    resetTransferTabs()
  }

  const handleLoginLogout = () => {
    if (!confirmLogout && attendantName) {
      setConfirmLogout(true)
    } else if (!attendantName) {
      if (cfdMode === MODES.POS) {
        resetCFDrequest()
      }
      dispatch(logout())
      dispatch(setRoute('/user-login'))
      resetTransferTabs()
    }
  }

  if (confirmLogout) {
    logoutModal = (
      <ConfirmModalV2
        headerText={'Logout?'}
        subtext={'Are you sure you would like to logout?'}
        buttonOneText={'CANCEL'}
        buttonTwoText={'LOGOUT'}
        onButtonOneClick={() => setConfirmLogout(false)}
        onButtonTwoClick={onClickLogout}
      />
    )
  }

  let logoutContainer = null

  if (attendantsExist) {
    logoutContainer = (
      <div className={styles.logout} onClick={handleLoginLogout}>
        <div className={styles.loggedInContainer}>
          <span className={styles.name}>{attendantName}</span>
          <ButtonBase disableRipple={isE700Device()} component="span" className={styles.logoutText}>
            {attendantName ? 'Logout' : 'Login'}
          </ButtonBase>
        </div>
      </div>
    )
  }

  const onClickMenu = () => {
    if (!isTabbed(orderInProgress)) {
      dispatch(setRoute('/concession-order'))
      resetTransferTabs()
      dispatch(resetCFD(false, true, true))
      return onToggle()
    }

    //we're in a tab and have items
    if (cartItemsExist) {
      setShowConfirmAbandonCartModal(true)
      abandonCartAction.current = () => {
        if (cfdMode === MODES.POS) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        }
        dispatch(setShouldIncrementOrderNumber())
        dispatch(clearCart())
        dispatch(clearOrderInProgress())
        dispatch(resetCFD(false, false))
        dispatch(setRoute('/concession-order'))
        resetTransferTabs()
        ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
      }
      return
    }

    //tab and no items
    dispatch(clearOrderInProgress())
    dispatch(resetCFD())
    dispatch(setRoute('/concession-order'))
    resetTransferTabs()
    return onToggle()
  }

  const onClickOrders = () => {
    if (isKiosk) {
      dispatch(setRoute('/menuSelection'))
      resetTransferTabs()
      return onToggle()
    }

    if (!cartItemsExist) {
      checkLocationAndStartSpinner('/orders')
      dispatch(clearOrderInProgress())
      dispatch(setRoute('/orders'))
      resetTransferTabs()
      return onToggle()
    }

    //tabbed order with items
    setShowConfirmAbandonCartModal(true)
    abandonCartAction.current = () => {
      if (cfdMode === MODES.POS) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      }
      dispatch(setShouldIncrementOrderNumber())
      checkLocationAndStartSpinner('/orders')
      dispatch(clearCart())
      dispatch(clearOrderInProgress())
      dispatch(setRoute('/orders'))
      resetTransferTabs()
      ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
    }
  }

  const onClickBalanceLookup = () => {
    if (!networkConnected) {
      dispatch(
        setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.INTERNET_CONNECTION_REQUIRED_AVAILABLE_BALANCE }),
      )
      return
    }

    resetTransferTabs()
    dispatch(setRoute('/balance-lookup'))
  }

  const navigateToConfiguration = () => {
    dispatch(clearOrderInProgress())
    dispatch(
      setRoute('/revenue-center-setup', {
        state: { prevLocation: history.location.pathname },
      }),
    )
    resetTransferTabs()

    return onToggle()
  }

  const onClickConfiguration = () => {
    const action = () => {
      if (cfdMode === MODES.POS) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      }

      if (cartItemsExist) {
        ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
      }

      dispatch(setShouldIncrementOrderNumber())
      dispatch(clearCart())

      if (!requireManagerPinForConfiguration) {
        return navigateToConfiguration()
      }

      dispatch(setManagerApprovalPrompt({ onSuccess: navigateToConfiguration, buttonText: 'Configuration' }))
    }

    if (!cartItemsExist) {
      action()
      return
    }

    setShowConfirmAbandonCartModal(true)
    abandonCartAction.current = action
  }

  const onClickSyncNetworkData = () => {
    if (!networkConnected) {
      dispatch(setConnectionRequiredDialog({ open: true }))
      return
    }

    const action = (shouldClearCart = false) => {
      if (cfdMode === MODES.POS) {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      }
      startSpinner()
      dispatch(refreshCache(shouldClearCart))
      resetTransferTabs()
      ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
    }

    if (!cartItemsExist) {
      startSpinner()
      dispatch(refreshCache())
      resetTransferTabs()
      return
    }

    setShowConfirmAbandonCartModal(true)
    abandonCartAction.current = action
  }

  const onClickResources = () => {
    //need to check if we're in a tab with items before exiting
    if (cartItemsExist) {
      setShowConfirmAbandonCartModal(true)
      abandonCartAction.current = () => {
        if (cfdMode === MODES.POS) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        }
        dispatch(setShouldIncrementOrderNumber())
        dispatch(clearCart())
        dispatch(clearOrderInProgress())
        dispatch(setRoute('/resources'))
        resetTransferTabs()
        ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
      }
      return
    }

    dispatch(setRoute('/resources'))
    return onToggle()
  }

  const onClickDebugInfo = () => {
    //need to check if we're in a tab with items before exiting
    if (isTabbed(orderInProgress) && cartItemsExist) {
      setShowConfirmAbandonCartModal(true)
      abandonCartAction.current = () => {
        if (cfdMode === MODES.POS) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        }
        dispatch(setShouldIncrementOrderNumber())
        dispatch(clearCart())
        dispatch(clearOrderInProgress())
        dispatch(setRoute('/debug-info'))
        resetTransferTabs()
        ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
      }
      return
    }

    resetTransferTabs()
    dispatch(setRoute('/debug-info'))
    return onToggle()
  }

  const onClickKiosk = () => {
    resetTransferTabs()
    dispatch(setRoute('/kiosk/attract_loop'))
    return onToggle()
  }

  const shoppingCartIcon = isMobileViewPort ? <MobileShoppingCartOutlinedIcon /> : <ShoppingCartOutlinedIcon />

  const abandonCartMessage = isMobileViewPort
    ? 'Items in cart will be removed upon syncing or navigating away from the menu. Do you wish to continue?'
    : 'Items in cart will be removed upon\n syncing or navigating away from the\n menu. Do you wish to continue?'

  //TODO T32579 Create a common abandon cart modal
  if (showConfirmAbandonCartModal) {
    confirmAbandonCartElement = (
      <ConfirmModalV2
        onButtonOneClick={() => {
          setShowConfirmAbandonCartModal(false)
          onToggle()
        }}
        onButtonTwoClick={() => {
          setShowConfirmAbandonCartModal(false)
          abandonCartAction.current(true)
          onToggle()
        }}
        headerText={shoppingCartIcon}
        subtext={abandonCartMessage}
        isKiosk={isKiosk}
        buttonOneText={'Cancel'}
        buttonTwoText={'Abandon Cart'}
      />
    )
  }

  const onClickRefreshConfig = (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (cartItemsExist) {
      setShowConfirmAbandonCartModal(true)
      abandonCartAction.current = () => {
        if (cfdMode === MODES.POS) {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        }
        dispatch(setShouldIncrementOrderNumber())
        dispatch(clearOrderInProgress())
        dispatch(clearCart())
        dispatch(refreshConfig())
        ToastManager.success(APPLICATION_STRINGS.CART_CLEARED)
      }
    } else {
      dispatch(clearOrderInProgress())
      dispatch(refreshConfig())
      onToggle()
    }
  }

  return (
    <div className={classNames}>
      {confirmAbandonCartElement}
      {logoutModal}
      <div className={styles.sidebarContainer}>
        <div className={styles.header}>
          <div className={styles.headerText}>Quick Service</div>
        </div>
        <div className={styles.links}>
          {DeviceSubtypes.isConcessions(deviceSubtype) && (
            <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickMenu} title="Menu">
              Menu
            </ButtonBase>
          )}
          {isKiosk && (
            <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickKiosk} title="Kiosk">
              Kiosk
            </ButtonBase>
          )}
          <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickOrders} title="Orders">
            Orders
          </ButtonBase>
          <ButtonBase
            disableRipple={isE700Device()}
            component="div"
            onClick={onClickBalanceLookup}
            title={APPLICATION_STRINGS.AVAILABLE_BALANCE_TITLE}
          >
            {APPLICATION_STRINGS.AVAILABLE_BALANCE_TITLE}
          </ButtonBase>
          <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickConfiguration} title="Configuration">
            Configuration
          </ButtonBase>
          {!isEmpty(resourceList) && (
            <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickResources} title="Resources">
              Resources
            </ButtonBase>
          )}
          <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickRefreshConfig} title="Refresh Config">
            Refresh Config
          </ButtonBase>
          <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickSyncNetworkData} title="Sync Network Data">
            Sync Network Data
          </ButtonBase>
          <ButtonBase disableRipple={isE700Device()} component="div" onClick={onClickDebugInfo} title="Debug Info">
            Debug Info
          </ButtonBase>
        </div>

        <div className={styles.appVerContainer}>
          {logoutContainer}
          <div className={styles.appVersion}>
            <span>{process.env.REACT_APP_VERSION || 'unknown'}</span>
          </div>
          {isMobileViewPort && <br />}
        </div>
      </div>
      <div className={styles.overlay} onClick={() => onToggle()} />
    </div>
  )
}

export default memo(NavMenu)
